import { z } from 'zod'

export const KNOWN_CURRENCIES = new Set(['EUR', 'USD', 'GBP', 'BRL', 'CHF'])

export const DocumentState = z.enum([
  'uploading',
  'received',
  'identified',
  'routed',
  'validated',
  'reconciled',
  'closed',
  'error',
])
export type DocumentState = z.infer<typeof DocumentState>

export const DocumentLabel = z.enum([
  // Generic flag — set when we need a human to look. Always set alongside
  // one of the reason-labels below so staff can filter by *why*. Kept for
  // backwards compatibility with the existing dashboard cards.
  'manual_review',
  // Reason for manual_review: AI's self-rated confidence is below the bar.
  'review:low_confidence',
  // Reason for manual_review: AI returned documentType='other' at >=0.5
  // confidence — the doc is legible but doesn't fit a known category.
  'review:unknown_doc_type',
  // Reason for manual_review: matched no client by NIF or alias.
  'review:no_client_match',
  // Reason for manual_review: matched a client alias ambiguously.
  'review:ambiguous_client_match',
  // Reason for manual_review: the emission date is implausible (AI wrong-year
  // failure) or contradicts the filename, and no fiscal QR resolved it.
  'review:date_uncertain',
  // Reason for manual_review: the printed date is ambiguous by ORDER — "8/11/2026"
  // is 11 August in the US and 8 November here — and nothing on the document
  // settles it. Unlike date_uncertain the date was NOT changed; we are declining
  // to guess, because guessing is what shipped the wrong dates before.
  'review:date_ambiguous',
  // Reason for manual_review: the AI's client NIF appears in neither field of
  // the fiscal QR (A issuer / B acquirer) — likely a misread.
  'review:nif_mismatch',
  // Reason for manual_review: the file appears to contain MORE THAN ONE invoice
  // (vision-reported, or a rasterised multi-page scan we only OCR'd page 1 of).
  // Forces review so a multi-invoice PDF never auto-routes on the first
  // invoice's value alone. Cleared on re-identify once it reads as one doc.
  'review:multi_invoice',
  // Reason for manual_review: this "document" is a download link from an email
  // body that we could not auto-download (unsupported provider / fetch failed).
  // Staff open the link via the "Ver" button and ingest the file manually.
  'review:link_only',
  // Reason for manual_review: a FOREIGN invoice bound for the TOConline Compras API,
  // held in Pendentes because the API isn't live yet (no per-client credentials). It
  // has no email fallback, so it waits for a human rather than sitting in Em Curso.
  'review:foreign_api_pending',
  // Reason for manual_review: the doc arrived from an unregistered sender
  // (email/WhatsApp number not on any client whitelist or portal user) and was
  // stored WITHOUT running AI identify. Staff confirm the sender and add it to
  // the whitelist, or contact the client. Only set when SENDER_GATE_ENABLED.
  'review:unauthorised_sender',
  'illegible',
  'duplicate_candidate',
  'foreign_supplier',
  // Routing guard for link-only placeholders: a doc with this label is shown in
  // Pendentes but the routing tick NEVER forwards it (it has no real file).
  // Cleared when staff release the doc to the pipeline via the edit dialog.
  'link_pending',
  // Reason for manual_review: the fiscal QR's acquirer NIF (field B) is valid but
  // differs from the matched client's NIF — likely mis-attribution.
  'review:qr_acquirer_mismatch',
  // Routing guard for consumidor-final docs (no valid buyer NIF): survives a manual
  // release/reassign — the routing tick NEVER forwards it. Re-derived on re-identify.
  'no_buyer_nif',
  // The client's OWN sale, proven by the fiscal QR (issuer = the client). Auto-filed to
  // Arquivo Fiscal and never routed: neither ERP's expense intake should book a sale.
  'auto_archived:venda',
])
export type DocumentLabel = z.infer<typeof DocumentLabel>

// AI extraction output — the shape the AI client promises to the DB layer.
// Every AI output MUST validate against this before any DB write.
export const DocumentExtraction = z.object({
  clientNif: z.string().regex(/^\d{9}$/, 'Portuguese NIF must be 9 digits'),
  // Used as a fallback to match against clients.aliases when clientNif
  // doesn't find a row. Optional — the AI leaves it null if unsure.
  recipientName: z.string().min(1).nullable().optional(),
  supplier: z.object({
    name: z.string().min(1),
    nif: z
      .string()
      .regex(/^\d{9}$/)
      .optional(),
    // Supplier country as ISO 3166-1 alpha-2 (PT, ES, FR, DE, US, GB…), read from
    // the supplier address/header. 'PT' (or omitted) = domestic. A confidently
    // non-PT value marks a FOREIGN invoice — which can't be filed via TOConline's
    // email/OCR intake and must go through the Compras API instead (isForeignPurchase).
    country: z.string().length(2).nullable().optional(),
    // Foreign supplier VAT/tax id VERBATIM (e.g. "ESB12345678", "FR12345678901") —
    // the PT-only 9-digit `nif` regex can't hold it. Feeds the Compras API
    // supplier_tax_registration_number. Null for a Portuguese supplier (use `nif`).
    taxId: z.string().min(1).nullable().optional(),
  }),
  // Document direction relative to the JA-Consultores client:
  //   - 'incoming': the firm's client is on the RECIPIENT block (purchase).
  //                 Matcher resolves the client via clientNif / recipientName.
  //   - 'outgoing': the firm's client is on the SUPPLIER block (sale).
  //                 Matcher pivots to supplier.nif / supplier.name. Recipient
  //                 is a third party we do not have a row for.
  // Null/absent for bank_statement and "other" — the matcher falls back to
  // incoming-style resolution in those cases.
  direction: z.enum(['incoming', 'outgoing']).nullable().optional(),
  issueDate: z.string().date(),
  // The emission date EXACTLY as printed (verbatim, not reordered). Lets us
  // re-resolve ambiguous orderings (YY-MM-DD vs DD-MM-YY) downstream.
  issueDateRaw: z.string().nullable().optional(),
  // Every OTHER date on the document (vencimento, data de carga…) — a fallback
  // for when the emission year is implausible. Mirror of the poller schema.
  additionalDates: z.array(z.string()).optional(),
  // Printed reference/series (e.g. "FT 2023A/1234") — corroborates the year.
  //
  // A value with NO alphanumeric character is coerced to null. Two shapes of the same
  // failure: the empty string (575 documents) and a lone punctuation mark the model
  // grabbed off the page — "/" 61 times, "." 19, "—" 15, also "," ">" ":" "+".
  //
  // This is not a new judgement about what counts as a reference. normalizeDocNumber
  // (efaturaReconcile.ts) already strips everything outside [A-Z0-9] and returns null
  // when nothing is left, so every matching path ALREADY treats these as absent. Only
  // storage disagreed, and the cost was ordering: they sort before every letter while a
  // real null sorts last, so blank references sat at BOTH ends of the Documento column.
  documentNumber: z
    .string()
    .nullable()
    .optional()
    .transform((v) => (v != null && !/[A-Za-z0-9]/.test(v) ? null : v)),
  // For bank_statement only: the issuing bank as printed on the statement header
  // (e.g. "Revolut", "Novo Banco"). Null for non-statements / when not identifiable.
  // Matched against clients.bank_accounts for per-bank statement tracking.
  bankName: z.string().min(1).nullable().optional(),
  // Statement HEADER fields (bank_statement only; null for every other type — the
  // poller's normalizeExtraction enforces it). Milestone A of the bank-reconciliation
  // plan (spec/plans/2026-09-23-bank-reconciliation-statement-fed.md): the coverage
  // period replaces month-truncated issueDate guessing, the balances anchor the
  // parse-time balance-identity check (opening + Σ movements = closing), and the
  // IBAN is the account key ("understand what is what" — a client may hold several
  // accounts at one bank). Header-level ONLY: the prompt still forbids enumerating
  // movements; line items come from the separate statement parser, never from here.
  statementPeriodStart: z.string().date().nullable().optional(),
  statementPeriodEnd: z.string().date().nullable().optional(),
  // SIGNED — an overdraft prints negative.
  openingBalance: z.number().nullable().optional(),
  closingBalance: z.number().nullable().optional(),
  // The ACCOUNT's IBAN as printed. Normalized here (strip separators, uppercase);
  // anything that doesn't look like an IBAN after normalization coerces to null so
  // a hallucinated fragment can never become an account key.
  iban: z
    .string()
    .nullable()
    .optional()
    .transform((v) => {
      if (v == null) return v
      const s = v.replace(/[\s.-]/g, '').toUpperCase()
      return /^[A-Z]{2}\d{2}[A-Z0-9]{8,30}$/.test(s) ? s : null
    }),
  // Nullable since 2026-09-16: a bank_statement has an opening balance, a closing
  // balance and a movement list — no document total. Asking for one anyway made the
  // field unstable (the same statement re-read as 3793.70 and then 37.01915), and
  // anything sorting/summing/reconciling on it was reading a meaningless number.
  // normalizeExtraction() nulls it for statement-shaped types; every consumer
  // already guards with `typeof x === 'number'`.
  totalAmount: z.number().nonnegative().nullable(),
  vatAmount: z.number().nonnegative().optional(),
  // Taxable base as PRINTED on the document (never derived by subtracting VAT from the
  // total — the prompt forbids it so the downstream net+VAT=total arithmetic check stays
  // independent). QR-decoded docs get their net from the QR I/J/K blocks instead.
  netAmount: z.number().nonnegative().optional(),
  // Whitelisted ISO 4217 codes the firm actually sees. Anything else (the AI
  // occasionally returns "XXX" or a stray locale symbol on invoices with no
  // explicit currency) coerces to EUR so the UI never renders the generic ¤
  // placeholder. Extend the set if a real new currency starts arriving.
  currency: z
    .string()
    .transform((v) => {
      const up = (v ?? '').trim().toUpperCase()
      return KNOWN_CURRENCIES.has(up) ? up : 'EUR'
    })
    .default('EUR'),
  documentType: z.enum([
    // Bookkeeping inputs — these route to the client's accounting system.
    'invoice',
    // Nota de Crédito / Nota de Débito. Separate types since 2026-09-16: until then
    // the enum could not express them and the prompt listed "nota de crédito" under
    // `invoice`, so all 280 that the AT confirms we hold were booked with the sign
    // reversed. Both are FISCAL — the ERP needs them exactly as it needs an invoice.
    'credit_note',
    'debit_note',
    'receipt',
    'bank_statement',
    'transfer_receipt', // Comprovativo de Transferência (one-line; NOT bank_statement)
    'travel_expense', // Bilhetes de Avião/Comboio / general expense claim
    'state_payment', // DUC / Multa
    'recibo_vencimento', // Recibo de Vencimento (payslip) — company_other, catalog-only (not routed)
    // 2026-07-27: mapa de km + ajudas de custo are now FISCAL (route to the ERP) — see document-category.ts.
    'mapa_quilometros', // Mapa de Quilómetros (mileage log)
    'ajudas_custo', // Ajudas de Custo (per-diem allowance)
    // Not bookkeeping material — router skips with reason
    // 'not_bookkeeping_material' (state → closed).
    'purchase_order', // Nota de Encomenda
    'transport_pass', // Passe (Lisboa Viva, metro)
    'meal_voucher_topup', // Carregamento Coverflex (Coverflex statements come separately)
    'financial_statement', // Balanço, Demonstração de Resultados (outputs, not inputs)
    'email_thread', // Forwarded email body, no attachment
    'signature_image', // Email-signature logo (instagram_*, youtube_*, image\d+.png)
    'back_of_invoice', // Single-page continuation with no header/NIF/totals
    'download_link', // Synthetic: a download link in an email body we couldn't fetch.
    // NEVER emitted by the AI — set by the poller on the link placeholder so the UI
    // labels it "Ligação para download".
    'other', // Genuine fall-through — triggers review:unknown_doc_type
  ]),
  // The CLIENT-side delivery/service/branch address printed on the document —
  // the store's supply address on a utility bill, the property served by a
  // repair invoice — as one verbatim free-form line. Distinct from the
  // supplier's own address AND from the buyer's fiscal/HQ address; null unless
  // such an address is clearly present. Input to tag rules ('delivery_address'
  // contains-conditions, e.g. routing costs to a business unit by its morada).
  deliveryAddress: z.string().min(1).nullable().optional(),
  // Line-item designações VERBATIM (descriptions only, no amounts/quantities).
  // Input to tag rules ('line_description' contains-conditions, e.g. cost
  // nature from what was bought). No .max() here — a 30-line invoice must not
  // fail validation; the poller truncates to 20 entries before persisting.
  lineDescriptions: z.array(z.string().min(1)).optional(),
  confidence: z.number().min(0).max(1),
  // True when the file clearly holds MORE THAN ONE distinct invoice/document
  // (several faturas scanned into one PDF, or two receipts on one page). Drives
  // multi-invoice handling in the poller: a multi-page PDF is page-split; a
  // non-page-sliceable file (image / single-page PDF) is auto-cropped into
  // per-invoice children and, when uncertain, sent to Pendentes
  // (review:needs_split) for the manual crop tool — never routed whole.
  // Optional/false for the normal one-document case.
  multipleDocumentsVisible: z.boolean().optional(),
})
export type DocumentExtraction = z.infer<typeof DocumentExtraction>
